export const BUSINESS_COLUMN_PREFS_KEY = "kbm.businesses.columns.v1";

/** Row actions are the only way into details/edit/files, so the column stays last and visible. */
export const PINNED_LAST_COLUMN_KEY = "actions";

/** A row without a name is unidentifiable, so this one may be moved but not hidden. */
export const LOCKED_COLUMN_KEYS: readonly string[] = [
  "businessName",
  PINNED_LAST_COLUMN_KEY,
];

/** The status dot column renders a blank header, so the picker needs a label of its own. */
const FALLBACK_COLUMN_LABELS: Readonly<Record<string, string>> = {
  onlineStatus: "Online status",
};

export interface BusinessColumnPrefs {
  order: string[];
  hidden: string[];
}

export interface BusinessColumnOption {
  key: string;
  label: string;
  locked: boolean;
  visible: boolean;
}

export const EMPTY_BUSINESS_COLUMN_PREFS: BusinessColumnPrefs = {
  order: [],
  hidden: [],
};

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string");
}

export function parseBusinessColumnPrefs(
  raw: string | null
): BusinessColumnPrefs {
  if (!raw) return EMPTY_BUSINESS_COLUMN_PREFS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return EMPTY_BUSINESS_COLUMN_PREFS;
    }
    const record = parsed as Record<string, unknown>;
    return {
      order: toStringArray(record.order),
      hidden: toStringArray(record.hidden),
    };
  } catch {
    return EMPTY_BUSINESS_COLUMN_PREFS;
  }
}

export function loadBusinessColumnPrefs(): BusinessColumnPrefs {
  try {
    return parseBusinessColumnPrefs(
      window.localStorage.getItem(BUSINESS_COLUMN_PREFS_KEY)
    );
  } catch {
    return EMPTY_BUSINESS_COLUMN_PREFS;
  }
}

export function saveBusinessColumnPrefs(prefs: BusinessColumnPrefs): void {
  try {
    window.localStorage.setItem(
      BUSINESS_COLUMN_PREFS_KEY,
      JSON.stringify(prefs)
    );
  } catch {
    // storage full or blocked — the table keeps working with in-memory prefs
  }
}

export function clearBusinessColumnPrefs(): void {
  try {
    window.localStorage.removeItem(BUSINESS_COLUMN_PREFS_KEY);
  } catch {
    // nothing to recover from; callers reset their own state
  }
}

/**
 * Stored keys the code no longer declares are dropped, and columns added since the
 * preference was saved come back at their default position.
 */
export function resolveColumnOrder(
  defaultKeys: string[],
  storedOrder: string[]
): string[] {
  const known = new Set(defaultKeys);
  const seen = new Set<string>();
  const resolved: string[] = [];

  storedOrder.forEach((key) => {
    if (!known.has(key) || seen.has(key)) return;
    seen.add(key);
    resolved.push(key);
  });

  defaultKeys.forEach((key, defaultIndex) => {
    if (seen.has(key)) return;
    seen.add(key);
    let insertAt = 0;
    for (let i = defaultIndex - 1; i >= 0; i--) {
      const precedingIndex = resolved.indexOf(defaultKeys[i]);
      if (precedingIndex !== -1) {
        insertAt = precedingIndex + 1;
        break;
      }
    }
    resolved.splice(insertAt, 0, key);
  });

  const pinnedIndex = resolved.indexOf(PINNED_LAST_COLUMN_KEY);
  if (pinnedIndex !== -1 && pinnedIndex !== resolved.length - 1) {
    resolved.splice(pinnedIndex, 1);
    resolved.push(PINNED_LAST_COLUMN_KEY);
  }

  return resolved;
}

export function resolveHiddenColumns(
  defaultKeys: string[],
  storedHidden: string[]
): string[] {
  const known = new Set(defaultKeys);
  return Array.from(
    new Set(
      storedHidden.filter(
        (key) => known.has(key) && !LOCKED_COLUMN_KEYS.includes(key)
      )
    )
  );
}

export function resolveColumnLabel(key: string, title: unknown): string {
  if (typeof title === "string" && title.trim()) return title;
  return FALLBACK_COLUMN_LABELS[key] ?? key;
}

interface KeyedColumn {
  key?: string | number;
  dataIndex?: unknown;
  title?: unknown;
}

/**
 * antd allows a column with only `dataIndex`, so fall back to it and then to a
 * positional id — a column with no addressable id must still reach the table.
 */
function resolveColumnId(column: KeyedColumn, index: number): string {
  if (column.key !== undefined) return String(column.key);
  if (
    typeof column.dataIndex === "string" ||
    typeof column.dataIndex === "number"
  ) {
    return String(column.dataIndex);
  }
  return `__column_${index}`;
}

function indexColumns<T extends KeyedColumn>(columns: T[]) {
  const byKey = new Map<string, T>();
  const defaultKeys: string[] = [];

  columns.forEach((column, index) => {
    const key = resolveColumnId(column, index);
    if (byKey.has(key)) return;
    byKey.set(key, column);
    defaultKeys.push(key);
  });

  return { byKey, defaultKeys };
}

export function applyBusinessColumnPrefs<T extends KeyedColumn>(
  columns: T[],
  prefs: BusinessColumnPrefs
): T[] {
  const { byKey, defaultKeys } = indexColumns(columns);
  const hidden = new Set(resolveHiddenColumns(defaultKeys, prefs.hidden));

  return resolveColumnOrder(defaultKeys, prefs.order)
    .filter((key) => !hidden.has(key))
    .map((key) => byKey.get(key))
    .filter((column): column is T => column !== undefined);
}

export function buildBusinessColumnOptions<T extends KeyedColumn>(
  columns: T[],
  prefs: BusinessColumnPrefs
): BusinessColumnOption[] {
  const { byKey, defaultKeys } = indexColumns(columns);
  const hidden = new Set(resolveHiddenColumns(defaultKeys, prefs.hidden));

  return resolveColumnOrder(defaultKeys, prefs.order).map((key) => ({
    key,
    label: resolveColumnLabel(key, byKey.get(key)?.title),
    locked: LOCKED_COLUMN_KEYS.includes(key),
    visible: !hidden.has(key),
  }));
}
